import React, { useEffect, useRef, useState } from 'react';

import { OnOffSwitch } from '../reusables/Switches';
import { TextInput } from '../reusables/FormElements';

import { ModuleFeatureValueType } from '../../interfaces/types';
import { IModuleFeatureWithSubscription } from '../../interfaces/services';

import { useGetSubscribedModuleFeatures, useSaveSubscribedModuleFeature } from '../../hooks/subscribed_module_features';

interface IModuleFeaturesProps {
    schoolId: number | null;
};
export const ModuleFeatures = (props: IModuleFeaturesProps) => {
    const { schoolId } = props;

    const { data: subscribedModuleFeatures, isLoading } = useGetSubscribedModuleFeatures(schoolId);

    return (
        <div className="immersion_admin__school__module_features">
            {isLoading &&
                <div className='immersion_admin__school__module_features__loader'>
                    <span className='immersion_admin__list_loader' />
                    <p>Loading features</p>
                </div>
            }
            {!isLoading && !subscribedModuleFeatures?.length &&
                <div className='immersion_admin__school__module_features__no_features'>
                    <h4>No features found</h4>
                    <p>Features will be displayed here.</p>
                </div>
            }
            {!!subscribedModuleFeatures?.length &&
                <>
                    <h4 className="color_primary">Module Features</h4>
                    <div className='immersion_admin__school__module_features__list'>
                        {subscribedModuleFeatures.map(feature =>
                            <div key={feature.id} className="immersion_admin__school__module_features__list__item">
                                {feature.module_features.map(moduleFeature =>
                                    <FeatureLabel
                                        key={moduleFeature.id}
                                        schoolId={schoolId}
                                        moduleFeature={moduleFeature}
                                    />
                                )}
                            </div>
                        )}
                    </div>
                </>
            }
        </div>
    );
};

interface IFeatureLabelProps {
    schoolId: number | null;
    moduleFeature: IModuleFeatureWithSubscription;
};
const FeatureLabel = (props: IFeatureLabelProps) => {
    const { schoolId, moduleFeature } = props;

    const debounceTimeout = useRef<NodeJS.Timeout | null>(null);

    const [integerText, setIntegerText] = useState<string>('');

    const { mutate: saveSubscribedModuleFeature } = useSaveSubscribedModuleFeature();

    useEffect(() => {
        if (moduleFeature.value_type === ModuleFeatureValueType.INTEGER) {
            setIntegerText(moduleFeature.subscribed_module_feature?.value ?? '');
        };
    }, [moduleFeature]);

    const saveValue = (value: string) => {
        if (!schoolId || !value?.trim()?.length) return;

        saveSubscribedModuleFeature({
            id: moduleFeature.subscribed_module_feature?.id ?? null,
            school_id: schoolId,
            module_feature_id: moduleFeature.id,
            value,
        });
    };

    const onChangeBoolean = () => {
        let value = '';
        if (!moduleFeature.subscribed_module_feature) {
            value = 'true';
        } else {
            value = moduleFeature.subscribed_module_feature.value === 'true' ? 'false' : 'true';
        };

        saveValue(value);
    };

    const onChangeText = (value: string) => {
        setIntegerText(value);

        if (debounceTimeout.current) {
            clearTimeout(debounceTimeout.current);
        };

        debounceTimeout.current = setTimeout(() => {
            saveValue(value);
        }, 700);
    };

    return (
        <div className="immersion_admin__school__module_features__list__item__label">
            <h5>{moduleFeature.title}</h5>
            {moduleFeature.value_type === ModuleFeatureValueType.BOOLEAN &&
                <OnOffSwitch checked={moduleFeature.subscribed_module_feature?.value === 'true'} onChange={onChangeBoolean} />
            }
            {moduleFeature.value_type === ModuleFeatureValueType.INTEGER &&
                <TextInput
                    name={moduleFeature.key}
                    type="text"
                    pattern_mask='integer'
                    placeholder="10"
                    value={integerText}
                    onChange={onChangeText}
                />
            }
        </div>
    );
};
